import type { CoverProductSelection } from './BookingDocket'
import { Product } from './Product'
import { SKU } from './SKU'
import { MenuLinkType } from './MenuLinkType'

export class Consumable extends Product {

    constructor(
        id: string,
        displayName: string,
        description: string | null,
        skus: SKU[],
        public modifierGroupIds: string[],
        dateCreated: Date,
        dateUpdated: Date,
        public imageId: string | null = null
    ) {
        super(id, displayName, description, skus, dateCreated, dateUpdated)
    }

    hasSKUsOnSale(): boolean {
        return this.skus.some(sku => sku.onSale)
    }

    getSKUWithId(skuId: string): SKU | null {
        return this.skus.find(sku => sku.id === skuId) || null
    }

    getAmountForSKU(skuId: string): number {
        return this.getSKUWithId(skuId)?.price ?? 0
    }
}

export class Menu {
    constructor(
        public id: string,
        public displayName: string,
        public description: string | null,
        public linkType: MenuLinkType,
        public menuCourses: MenuCourse[],
        public dateCreated: Date,
        public dateUpdated: Date,
        public minimumCoursesRequired: number | null = null,
        public maximumCoursesRequired: number | null = null,
        public active: boolean = true,
        public isPaid: boolean = false
    ) {}

    findItemForSelection(selection: CoverProductSelection): MenuItem | null {
        // Legacy choices use the first SKU match in menu order.
        return this.menuCourses.flatMap(course => course.menuItems).find(item =>
            item.skuId === selection.productSkuId && (!selection.menuItemId || item.id === selection.menuItemId)
        ) ?? null
    }

    findCourseForSelection(selection: CoverProductSelection): MenuCourse | null {
        const item = this.findItemForSelection(selection)
        return item ? this.menuCourses.find(course => course.id === item.menuCourseId) ?? null : null
    }

    findMenuItemBySkuId(skuId: string): MenuItem | null {
        for (const course of this.menuCourses) {
            const menuItem = course.menuItems.find(item => item.skuId === skuId)
            if (menuItem) {
                return menuItem
            }
        }
        return null
    }

    sortedCourseNames(): string[] {
        return [...this.menuCourses]
            .sort((a, b) => a.orderIndex - b.orderIndex)
            .map(c => c.displayName)
    }
}

export class MenuCourse {
    constructor(
        public id: string,
        public menuId: string,
        public displayName: string,
        public orderIndex: number,
        public minimumItemsRequired: number | null,
        public maximumItemsRequired: number | null,
        public menuItems: MenuItem[],
        public excludesFromCourseCount: boolean = false,
        public description: string | null = null
    ) {}
}

export class MenuItem {
    constructor(
        public id: string,
        public menuId: string,
        public menuCourseId: string,
        public consumableId: string,
        public skuId: string,
        public orderIndex: number,
        public refundable: boolean = true,
        public paidOnline: boolean = true
    ) {}
}
